import { useProgramData } from "@features/program/useProgramData"
import { FilmSessionFragment, FilmTileFragment } from "@generated-types"
import dayjs, { Dayjs } from "dayjs"
import { useMemo } from "react"
import { useSnapshot } from "valtio"
import { FerveCartItem } from "./ferve-cart"
import { PurchasedTicket } from "./ferve-wishlist"
import { useFerve } from "./FerveProvider"

export type PlannerItem = {
  session: FilmSessionFragment
  film: FilmTileFragment
  cartItem?: FerveCartItem
  purchasedTickets?: PurchasedTicket
  clashWith?: { previous: boolean; next: boolean }
  isWishlist?: boolean
  date: Dayjs
  endDate?: Dayjs
}

export function useResolvedPlanner(): PlannerItem[] {
  const { wishlist, cart } = useFerve()
  const wishlistState = useSnapshot(wishlist.state)

  const wishlistIds = wishlistState.hashes
  const cartItems = useSnapshot(cart.state)?.data?.items ?? []
  const purchases = wishlistState.purchased

  const program = useProgramData()

  const items = useMemo(() => {
    const items: PlannerItem[] = []

    for (const film of program.data?.currentProgram?.films ?? []) {
      if (!film) continue

      for (const session of film.sessions ?? []) {
        if (!session) continue
        const date = dayjs(session.info?.dateTime)
        const endDate = dayjs(session.info?.dateTime).add(film.eventInfo?.runtime ?? 0, "minutes")
        let matched = false
        for (const cartItem of cartItems) {
          if (cartItem.title === film.title && session.info?.dateTime === cartItem.ferveDate) {
            matched = true
            items.push({
              session,
              film,
              cartItem: cartItem as FerveCartItem,
              date,
              endDate,
            })
            break
          }
        }
        if (matched) continue

        for (const purchase of purchases) {
          if (purchase.sessionHash === session.info?.hash) {
            items.push({
              session,
              film,
              purchasedTickets: purchase as PurchasedTicket,
              date,
              endDate,
            })
            matched = true
            break
          }
        }

        if (matched) continue

        for (const id of wishlistIds) {
          if (String(session.info?.ferveId) === id) {
            items.push({
              session,
              film,
              isWishlist: true,
              date,
              endDate,
            })
            matched = true
            break
          }
        }
      }
    }

    items.sort((a, b) => {
      return a.date.diff(b.date, "seconds")
    })

    for (let i = 0; i < items.length; i++) {
      const previous = items[i - 1]
      const next = items[i + 1]

      const currentStart = items[i].date
      const currentEnd = items[i].endDate

      let clashWithPrevious = previous?.endDate && currentStart.isBefore(previous?.endDate)
      let clashWithNext = next?.date && currentEnd && next.date.isBefore(currentEnd)

      items[i].clashWith = { previous: !!clashWithPrevious, next: !!clashWithNext }
    }

    return items
  }, [wishlistIds, cartItems, program.data, purchases])

  return items
}
